import React, { useEffect, useRef, useState } from 'react';
import { Play, FileAudio } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import { audioTyp } from '../../utils/anhangArt';
import './AudioAnhang.css';

// #508: Player und „Transkribieren“ an einem Audio-Anhang (Mail oder Ticket,
// am Arbeitsplatz und mobil). Die Datei wird erst auf Klick geladen, als Blob
// mit Audio-Typ: Der Server liefert sie als octet-stream (#281), und ein
// Abruf über die API trägt die Anmeldung auch für API-Keys. `media-src` der
// CSP erlaubt `blob:`.
//
// `onTranskribieren` fehlt, wenn es den Knopf nicht geben soll (Schalter
// `voicemailAi` aus, kein Schreibrecht, Mail-Ansicht ohne Ticket). Das
// Ergebnis ist ein interner Kommentar; der Container lädt danach den Verlauf.

const FEHLER = {
  aiDisabled: ['audio.fehlerAus', 'Die Audio-KI ist abgeschaltet.'],
  aiNotConfigured: ['audio.fehlerNichtEingerichtet', 'Die KI ist nicht eingerichtet.'],
  laeuftNoch: ['audio.fehlerLaeuft', 'Diese Datei wird gerade schon transkribiert.'],
  leeresTranskript: ['audio.fehlerLeer', 'In der Aufnahme war nichts zu verstehen.'],
  zuGross: ['audio.fehlerGross', 'Die Datei ist größer als 25 MB.'],
  ohneInhalt: ['audio.fehlerOhneInhalt', 'Der Inhalt der Datei liegt nicht vor.'],
  ticketArchiviert: ['audio.fehlerArchiviert', 'Das Ticket ist abgeschlossen.'],
  foreignTicket: ['audio.fehlerRecht', 'Keine Berechtigung für dieses Ticket.'],
  anbieterFehler: ['audio.fehlerAnbieter', 'Die Transkription ist beim KI-Anbieter fehlgeschlagen.']
};

export function audioFehlerText(t, err) {
  const code = err?.response?.data?.code;
  const eintrag = FEHLER[code] || (!err?.response ? ['audio.fehlerNetz', 'Keine Verbindung zum Server.'] : ['audio.fehlerAllgemein', 'Die Transkription ist fehlgeschlagen.']);
  return t(eintrag[0], eintrag[1]);
}

const AudioAnhang = ({ anhang, laden, onTranskribieren }) => {
  const { t } = useI18n();
  const [url, setUrl] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [transkribiert, setTranskribiert] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [fertig, setFertig] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const abspielen = async () => {
    setLaedt(true);
    setLadeFehler(false);
    try {
      const blob = await laden();
      const neu = URL.createObjectURL(new Blob([blob], { type: audioTyp(anhang) }));
      urlRef.current = neu;
      setUrl(neu);
    } catch (_) {
      setLadeFehler(true);
    } finally {
      setLaedt(false);
    }
  };

  const transkribieren = async () => {
    setTranskribiert(true);
    setFehler(null);
    setFertig(false);
    try {
      await onTranskribieren();
      setFertig(true);
    } catch (err) {
      setFehler(audioFehlerText(t, err));
    } finally {
      setTranskribiert(false);
    }
  };

  const name = anhang?.name || anhang?.originalName || '';
  return (
    <div className="audio-anhang" onClick={(e) => e.stopPropagation()}>
      <div className="audio-anhang-knoepfe">
        {!url && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={abspielen} disabled={laedt}
            aria-label={t('audio.abspielenName', 'Abspielen: {{name}}', { name })}>
            <Play size={14} aria-hidden="true" /> {laedt ? t('audio.laedt', 'Lädt…') : t('audio.abspielen', 'Abspielen')}
          </button>
        )}
        {onTranskribieren && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={transkribieren} disabled={transkribiert}
            aria-label={t('audio.transkribierenName', 'Transkribieren: {{name}}', { name })}>
            <FileAudio size={14} aria-hidden="true" /> {transkribiert ? t('audio.transkribiertGerade', 'Transkribiert…') : t('audio.transkribieren', 'Transkribieren')}
          </button>
        )}
      </div>
      {url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio className="audio-anhang-player" controls autoPlay src={url} aria-label={name} />
      )}
      {ladeFehler && <p className="audio-anhang-fehler" role="alert">{t('audio.ladeFehler', 'Die Datei ließ sich nicht laden.')}</p>}
      {fehler && <p className="audio-anhang-fehler" role="alert">{fehler}</p>}
      {fertig && <p className="audio-anhang-ok" role="status">{t('audio.fertig', 'Transkript als interner Kommentar angelegt.')}</p>}
    </div>
  );
};

export default AudioAnhang;
